import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useHotel } from "@/lib/hotel";

const WORKSPACE_LABEL = {
  patient: "Guest workspace",
  doctor: "Clinician workspace",
  hotel_staff: "Hotel workspace",
  hotel_manager: "Hotel workspace",
  platform_admin: "Platform workspace",
  pharmacy: "Pharmacy workspace",
  laboratory: "Laboratory workspace",
  courier: "Courier workspace",
  specimen_collector: "Collector workspace",
};

const SEARCH_PLACEHOLDER = {
  patient: "Search appointments, prescriptions…",
  doctor: "Search patients, appointments…",
  hotel_staff: "Search rooms, bookings…",
  hotel_manager: "Search rooms, bookings, folios…",
  platform_admin: "Search doctors, hotels, users…",
  pharmacy: "Search orders…",
  laboratory: "Search orders, specimens…",
};

/**
 * Desktop-only workspace top bar (search + context chip + avatar) rendered
 * once per portal from <RoleNav/> in App.js. Mirrors the "AllRound Health
 * Concierge" mockup's topbar pattern across every non-marketing surface.
 * Purely presentational — search input is not wired to a backend query yet.
 */
export default function WorkspaceTopBar({ testid }) {
  const { user, logout } = useAuth() || {};
  const { ctx } = useHotel() || {};
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const contextLabel =
    user.role === "patient" && ctx?.room?.room_number
      ? `Room ${ctx.room.room_number} · ${ctx?.hotel?.name || "Guest"}`
      : WORKSPACE_LABEL[user.role] || "Workspace";

  const name = user.full_name || user.email || "Account";
  const initials = name
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "A";

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success("Signed out");
    } catch {
      // best-effort
    }
    nav("/", { replace: true });
  };

  return (
    <div className="workspace-topbar" data-testid={testid || "workspace-topbar"}>
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
        <input
          type="text"
          placeholder={SEARCH_PLACEHOLDER[user.role] || "Search…"}
          className="workspace-search pl-10"
          data-testid="workspace-search-input"
          onFocus={(e) => e.currentTarget.select()}
        />
      </div>
      <div className="flex-1" />
      <div className="context-chip" data-testid="workspace-context-chip">
        <span className="dot" />
        {contextLabel}
      </div>
      <div className="relative">
        <button
          type="button"
          className="topbar-avatar"
          data-testid="workspace-avatar-btn"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="avatar-circle">{initials}</span>
          <span className="hidden xl:block text-left leading-tight">
            <span className="block text-sm font-medium text-stone-900 truncate max-w-[9rem]">{name}</span>
            <span className="block text-[10px] uppercase tracking-widest text-stone-500">{(user.role || "").replace(/_/g, " ")}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full mt-2 w-48 card-lux p-1.5 z-40" data-testid="workspace-avatar-menu">
              <button
                type="button"
                onClick={handleSignOut}
                data-testid="workspace-signout-btn"
                className="w-full inline-flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-stone-700 hover:text-red-700 hover:bg-red-50 transition"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
